import { useCallback, useEffect, useMemo } from "react";
import { useTeamsStore, User } from "@/store/teamsStore";
import { EntitySelector } from "@/components/EntitySelector";

type UserSelectorBaseProps = {
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  size?: "default" | "sm";
};

type SingleUserSelectorProps = UserSelectorBaseProps & {
  mode?: "single";
  currentlySelectedId: string | null;
  onSelectId: (userId: string | null) => void;
};

type MultiUserSelectorProps = UserSelectorBaseProps & {
  mode: "multi";
  selectedUserIds: string[];
  onUserIdsChange: (userIds: string[]) => void;
};

type UserSelectorProps = SingleUserSelectorProps | MultiUserSelectorProps;

function useUserSelectorData() {
  const { users, loading, fetchUsers } = useTeamsStore();

  const usersMap = useMemo(() => {
    const map = new Map<string, User>();
    users.forEach((user) => {
      map.set(user.id, user);
    });
    return map;
  }, [users]);

  const fetchEntities = useCallback(
    async (search?: string) => {
      if (!search) {
        await fetchUsers();
      }
      const currentUsers = useTeamsStore.getState().users;
      if (!search) {
        return currentUsers.map((u) => u.id);
      }
      const searchLower = search.toLowerCase();
      return currentUsers
        .filter((u) => {
          const fullName = [u.firstName, u.lastName]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return (
            fullName.includes(searchLower) ||
            u.email.toLowerCase().includes(searchLower)
          );
        })
        .map((u) => u.id);
    },
    [fetchUsers],
  );

  const getDisplayName = useCallback((user: User) => {
    const parts = [user.firstName, user.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : user.email;
  }, []);

  const getSecondaryInfo = useCallback((user: User) => {
    const parts: string[] = [];
    if (user.email) {
      parts.push(user.email);
    }
    if (user.role) {
      parts.push(user.role);
    }
    return parts.join(" • ");
  }, []);

  return {
    usersMap,
    loading,
    fetchUsers,
    fetchEntities,
    getDisplayName,
    getSecondaryInfo,
  };
}

export function UserSelector(props: UserSelectorProps) {
  const {
    placeholder = "Select a user...",
    className,
    disabled = false,
    size = "default",
  } = props;

  const {
    usersMap,
    loading,
    fetchUsers,
    fetchEntities,
    getDisplayName,
    getSecondaryInfo,
  } = useUserSelectorData();

  const hasMissingUsers =
    props.mode === "multi"
      ? props.selectedUserIds.some((id) => !usersMap.has(id))
      : props.currentlySelectedId !== null &&
        !usersMap.has(props.currentlySelectedId);

  useEffect(() => {
    if (hasMissingUsers) {
      fetchUsers();
    }
  }, [hasMissingUsers, fetchUsers]);

  const sharedProps = {
    entities: usersMap,
    listLoading: loading,
    fetchEntities,
    getDisplayName,
    getSecondaryInfo,
    placeholder,
    searchPlaceholder: "Search users...",
    className,
    disabled,
    size,
    entityLabel: "users",
  } as const;

  const modeProps =
    props.mode === "multi"
      ? ({
          mode: "multi",
          selectedIds: props.selectedUserIds,
          onSelectedIdsChange: props.onUserIdsChange,
        } as const)
      : ({
          currentlySelectedId: props.currentlySelectedId,
          onSelectId: props.onSelectId,
        } as const);

  return <EntitySelector<User> {...sharedProps} {...modeProps} />;
}
